import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { LinearProgress, Switch } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IAppState } from '../../../../state/reducers';
import {
  IState,
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import {
  queryExportTemplateSettings,
  toggleExportTemplateSettings,
  saveSettings,
  updateOriginalFilename,
} from '../../../../state/exchange/exporttemplateSettings';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogActionsOverride,
  ActionContainer,
} from '../../../Shared/styles/dialog';
import { VaultButton } from '../../../Shared';
import {
  ProgressContainer,
  Label,
  Preview,
  DropContainer,
  StyledChip,
  DroppableContainer,
  UnusedNotice,
  OriginalFileNameContainer,
} from './styles';
import {
  INamingTemplatePart,
} from '../../../../api/vault/namingTemplates/interfaces';
import { Styling } from '../../../../constants/styling';

// Reorder when dropping into the same list
const reorder = (list: INamingTemplatePart[], startIndex: any, endIndex: any) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source: any, destination: any, droppableSource: any, droppableDestination: any) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result: any = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const getListStyleSource = (isDraggingOver: any) => ({
  display: 'flex',
  flexWrap: 'wrap' as 'wrap',
  padding: `${Styling.DEFAULT_SPACING_DOUBLED}px ${Styling.DEFAULT_SPACING_DOUBLED}px ${Styling.DEFAULT_SPACING}px ${Styling.DEFAULT_SPACING_DOUBLED}px`,
});

const getListStyle = (isDraggingOver: any) => ({
  background: isDraggingOver ? '#f1f1f1' : 'inherit',
  display: 'flex',
  flexWrap: 'wrap' as 'wrap',
  padding: `${Styling.DEFAULT_SPACING}px ${Styling.DEFAULT_SPACING_DOUBLED}px ${Styling.DEFAULT_SPACING_DOUBLED}px ${Styling.DEFAULT_SPACING_DOUBLED}px`,
});

class ExportTemplateSettings extends React.Component<IProps, IState> {
  unusedItems: INamingTemplatePart[];
  selectedItems: INamingTemplatePart[];
  example: string;

  constructor(props: any) {
    super(props);
    this.onDragEnd = this.onDragEnd.bind(this);

    this.unusedItems = [];
    this.selectedItems = [];

    this.example = '';
  }

  async componentDidMount() {
    // TODO clear props at some point before launch of dialog
    // or hold off on display until data comes back from service.
    const { namingTemplate } = this.props;
    namingTemplate.length = 0; // Clear

    const { onNamingTemplateQuery } = this.props;
    await onNamingTemplateQuery();
  }

  componentWillUnmount() {
    // TODO clear props at some point before launch of dialog
    // or hold off on display until data comes back from service.
    const { namingTemplate } = this.props;
    namingTemplate.length = 0; // Clear
  }

  onDelete(id: string) {
    const { namingTemplate } = this.props;

    if (id == null || id === '') {
      return;
    }

    // Remove item and mark it 'not in use'.
    const item = this.selectedItems.filter((obj) => obj._id === id)[0];
    item.isUsed = false;
    this.unusedItems.push(item);

    this.selectedItems = this.selectedItems.filter((obj) => obj._id !== id);

    namingTemplate.length = 0;
    this.selectedItems.map((value) => (
      namingTemplate.push(value)));
    this.unusedItems.map((value) => (
      namingTemplate.push(value)));

    // ensure unused items are sorted alphabetically
    this.unusedItems = this.unusedItems
      .sort((a: INamingTemplatePart, b: INamingTemplatePart) => a.displayName.localeCompare(b.displayName));

    this.updatePreview();

    // TEMP TODO need to update preview UI widget
    let temp: string;
    const myState: IState = { preview: temp };
    this.setState(myState);
  }

  getList = (id: any) => (id === 'droppableNotUsed' ? this.unusedItems : this.selectedItems);

  refreshLists() {
    const { namingTemplate } = this.props;
    const currentNamingTemplate = [...namingTemplate];
    this.unusedItems = currentNamingTemplate.filter((value) => value.isUsed === false).sort((n1, n2) => n1.displayName.localeCompare(n2.displayName));
    this.selectedItems = currentNamingTemplate.filter((value) => value.isUsed === true).sort((n1, n2) => n1.order - n2.order).map((e) => {
      e.order = -1;
      return e;
    });
    // We do not use the index after loading. Drag and drop will
    // change the order and we do not need to keep track of it.
    // The final order will be picked up upon Save.
    this.updatePreview();
  }

  updatePreview() {
    this.example = '';
    let s: string = '';
    this.selectedItems.forEach((value) => {
      s += value.sampleValue;
      s += '  ';
    });

    this.example = s || 'N/A';
  }

  onDragStart(result: any) {
    const { draggableId } = result;
    if (this != null) {
      const item = this.unusedItems.filter((obj) => obj._id === draggableId);
      item[0].isUsed = true;
    }
  }

  onDragEnd(result: any) {
    const { source, destination } = result;
    const { namingTemplate } = this.props;

    // dropped outside the list
    if (!result.destination) {
      return;
    }

    // First, anything dropped into the same list
    if (source.droppableId === destination.droppableId) {
      const items = reorder(
        this.getList(source.droppableId),
        source.index,
        destination.index,
      );

      if (source.droppableId === 'droppableSelected') {
        this.selectedItems = items;
      } else {
        this.unusedItems = items;
      }

      namingTemplate.length = 0;
      this.selectedItems.map((value) => (
        namingTemplate.push(value)));
      this.unusedItems.map((value) => (
        namingTemplate.push(value)));
    } else { // Dropped into a different list
      const result2 = move(
        this.getList(source.droppableId),
        this.getList(destination.droppableId),
        source,
        destination,
      );

      this.unusedItems = result2.droppableNotUsed;
      this.selectedItems = result2.droppableSelected;

      namingTemplate.length = 0;
      this.selectedItems.map((value) => (
        namingTemplate.push(value)));
      this.unusedItems.map((value) => (
        namingTemplate.push(value)));
    }

    // Insure item is set to 'not in use' when moved to NotUsed
    // and set to 'in use' when moved to Selected.
    if (destination.droppableId === 'droppableNotUsed') {
      this.unusedItems[destination.index].isUsed = false;
    } else if (destination.droppableId === 'droppableSelected') {
      this.selectedItems[destination.index].isUsed = true;
    }

    // ensure unused items are sorted alphabetically
    this.unusedItems = this.unusedItems
      .sort((a: INamingTemplatePart, b: INamingTemplatePart) => a.displayName.localeCompare(b.displayName));

    let s: string = '';
    this.selectedItems.forEach((value) => {
      if (s !== '') { // TODO: Spacer is two spaces for now
        s += '  ';
      }
      s += value.sampleValue;
    });

    this.example = s;

    // Update, use this for now...
    const newState = {
      ...this.state,
    };

    this.setState(newState);
  }

  renderOriginalFileNameSwitch() {
    const { fullNamingTemplate, onUpdateOriginalFilename } = this.props;
    if (!fullNamingTemplate) {
      return <></>;
    }
    const { originalFileName } = fullNamingTemplate;
    return (
      <OriginalFileNameContainer>
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => onUpdateOriginalFilename(!originalFileName)}
          // Needed to add to pass lint
          onKeyDown={() => { }}
          tabIndex={0}
          role="button"
        >Use Original File Name
        </span>
        <div>
          <Switch color="primary" checked={originalFileName} onChange={(e) => onUpdateOriginalFilename(e.target.checked)} data-tip="originalFileName" data-value={originalFileName} />
        </div>
      </OriginalFileNameContainer>
    );
  }

  render() {
    const {
      isLoading,
      onSave,
      onClose,
      fullNamingTemplate,
    } = this.props;

    let originalFileName = false;
    if (fullNamingTemplate && fullNamingTemplate.originalFileName) {
      originalFileName = true;
    }

    // Initializing. Drag and drop handler functions will take care
    // of the positions of items after this.
    if (this.unusedItems.length === 0 && this.selectedItems.length === 0) {
      this.refreshLists();
    }

    return (
      <>
        <DialogHeader
          data-auto="ExportTemplateDialog"
        >
          <DialogHeaderTitle>
            Export File Name
          </DialogHeaderTitle>
        </DialogHeader>
        <ProgressContainer>
          {isLoading && <LinearProgress />}
        </ProgressContainer>
        <>
          <DropContainer
            className="DropContainerParent"
          >
            <DragDropContext onDragEnd={this.onDragEnd} onDragStart={this.onDragStart}>
              <Droppable droppableId="droppableNotUsed" direction="horizontal" isDropDisabled={false}>
                {(provided, snapshot) => (
                  <DroppableContainer
                    className="DroppableContainer1"
                    ref={provided.innerRef}
                    style={getListStyleSource(snapshot.isDraggingOver)}
                  >

                    {this.unusedItems.length === 0 && (
                      <UnusedNotice>
                        <DragIndicator /> Drag unwanted parts here
                      </UnusedNotice>
                    )}
                    {this.unusedItems.map((item: INamingTemplatePart, index: any) => (
                      <Draggable
                        key={item._id}
                        draggableId={item._id}
                        index={index}
                      >
                        {(provided2, snapshot2) => (
                          <StyledChip
                            ref={provided2.innerRef}
                            {...provided2.draggableProps}
                            {...provided2.dragHandleProps}
                            label={item.displayName ? item.displayName : item.tagName}
                            key={item._id}
                            className={item.isUsed ? 'focused' : ''}
                            disabled={originalFileName}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </DroppableContainer>
                )}
              </Droppable>

              <Droppable droppableId="droppableSelected" direction="horizontal" isDropDisabled={false}>
                {(provided, snapshot) => (
                  <DroppableContainer
                    className="DroppableContainer2"
                    ref={provided.innerRef}
                    style={getListStyle(snapshot.isDraggingOver)}
                  >
                    {this.selectedItems.length === 0 && (
                      <UnusedNotice>
                        <DragIndicator /> Drag wanted parts here
                      </UnusedNotice>
                    )}
                    {this.selectedItems.map((item: INamingTemplatePart, index: any) => (
                      <Draggable
                        key={item._id}
                        draggableId={item._id}
                        index={index}
                      >
                        {(provided2, snapshot2) => (
                          <StyledChip
                            ref={provided2.innerRef}
                            {...provided2.draggableProps}
                            {...provided2.dragHandleProps}
                            label={item.displayName ? item.displayName : item.tagName}
                            key={item._id}
                            className="focused"
                            onDelete={() => this.onDelete(item._id)}
                            disabled={originalFileName}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </DroppableContainer>
                )}
              </Droppable>
            </DragDropContext>
          </DropContainer>
          <Label
            data-auto="FileNamePreviewLabel"
            className={originalFileName ? 'disabled' : ''}
          >
            File Name Preview:
            <br />
            <Preview>{this.example}</Preview>
          </Label>
        </>
        {this.renderOriginalFileNameSwitch()}
        <DialogActionsOverride disableSpacing>
          <ActionContainer>
            <VaultButton
              variant="plainOnPaper"
              onClick={onClose}
              data-auto="CancelTemplateBtn"
            >
              Cancel
            </VaultButton>
            <VaultButton
              variant="primaryOnWhite"
              onClick={() => { onSave(); onClose(); }}
              data-auto="SaveTemplateBtn"
            >
              Save
            </VaultButton>
          </ActionContainer>
        </DialogActionsOverride>
      </>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isLoading: state.exchange.exporttemplatesettings.isLoading,
  namingTemplate: state.exchange.exporttemplatesettings.namingTemplate,
  _id: state.exchange.exporttemplatesettings._id,
  preview: state.exchange.exporttemplatesettings.preview,
  fullNamingTemplate: state.exchange.exporttemplatesettings.fullNamingTemplate,
});

const mapDispatchToProps: IDispatchProps = {
  onNamingTemplateQuery: queryExportTemplateSettings,
  onSave: saveSettings,
  onClose: toggleExportTemplateSettings,
  onUpdateOriginalFilename: updateOriginalFilename,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ExportTemplateSettings);
